import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'; 
import nexapi from '../../services/api';
import { ApplicationModel } from '../../types/models/ApplicationModel';
import { notify } from '../../components/UI/notify';
import { NotifyType } from '../../types/enums/notifytype';
import {RequestStatus} from '../slices/commonEnum';

interface ApplicationState {
  data: ApplicationModel[];
  totalRecords: number;
  currentPage: number;
  loading: boolean;
  error: string | null;
  status: string | null;
}

interface ApplicationResponse {
  applications: ApplicationModel[]; // Change to ApplicationModel array instead of any[]
  totalCount: number;
}

// API URL
const API_URL = '/Application';
const { message } = notify();

export const fetchApplications = createAsyncThunk<ApplicationResponse, {
  filters?: Record<string, any>; 
  page?: number; 
  numberOfRecord?: number;
  sortColumn?: string;
  sortOrder?: 'asc' | 'desc';
}>(
  'applications/fetchApplications',
  async ({ filters, page = 1, numberOfRecord, sortColumn = '', sortOrder = 'asc' }) => {
    try {
      const isPaginationEnabled = typeof numberOfRecord === 'number';

      const url = API_URL + `/GetAll?` +
      `pagination=${isPaginationEnabled}` +
      (isPaginationEnabled ? `&page=${page}&numberOfRecord=${numberOfRecord}` : '') +
      `&search=${filters.search ?? ""}` +
      `&sortColumn=${sortColumn}` +
      `&sortOrder=${sortOrder}` + 
      `&state=${filters.state ?? ""}`;
      const response = await nexapi.post(url);
      // const response = await nexapi.post(API_URL + `/GetAll?pagination=${isPaginationEnabled}&page=${page}&numberOfRecord=${numberOfRecord}&search=${filters.search ?? ""}&sortColumn=${sortColumn}&sortOrder=${sortOrder}`);
      const data = response.data;

      if (Array.isArray(data?.data)) {
        return {
          applications: data.data, // The list of applications
          totalCount: data.totalCount, // The total count of applications
        };

      } else {
        console.error("Invalid API Response:", data);
        return { applications: [], totalCount: 0 }; // Return a fallback structure
      }
    } catch (error) {
      console.error("API Call Failed:", error);
      return { applications: [], totalCount: 0 };
    }
  }
);


export const getApplicationById = createAsyncThunk(
  'applications/getApplicationById',
  async ({ id }: { id: string }) => {
    const response = await nexapi.get(
      API_URL + `/${id}`
    );
    return {
      applications: response.data,
    };
  }
);


export const createApplication = createAsyncThunk('applications/createApplication', async (application:ApplicationModel) => {
  const response = await nexapi.post(API_URL, application);
  message(NotifyType.Success, "Application created successfully")
  return response.data;
});

export const updateApplication = createAsyncThunk('applications/updateApplication', async (application:ApplicationModel ) => {
  const response = await nexapi.put(`${API_URL}/${application.id}`, application);
  message(NotifyType.Success, "Application updated successfully")
  return response.data;
});

// export const deleteApplication = createAsyncThunk('applications/deleteApplication', async (id:any) => {
//   try {
//   await nexapi.delete(`${API_URL}/${id}`);
//   return id;
// } catch (error) {
//   return error.message
// }
// });


export const deleteApplication = createAsyncThunk<ApplicationResponse, { Id : string, UserId:string,reason: string}>(
  'applications/deleteApplication',
  async ({ Id,UserId,reason}) => {
    
    try {
      const [cleanId] = Id.includes('_') ? Id.split('_') : [Id];

      const payload = {
        Id: cleanId,
        UserId: UserId,
        reason: reason
      };
      const url = API_URL+"/ActivateDeactivateApplication";
      const method = "post";
      const response = await nexapi[method](url, payload);

      
      response.data.statusCode = "200";
      return response.data.statusCode;
    } catch (error) {
      console.error('API Call Failed:', error);
      throw error;
    }
  }
);

const initialState: ApplicationState = {
  data: [],
  totalRecords: 0,
  currentPage: 0,
  loading: false,
  error: null,
  status: RequestStatus.Status,
};

// Slice
const applicationsSlice = createSlice({
  name: 'applications',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchApplications.fulfilled, (state, action) => {
        // Assuming action.payload contains applications and totalCount
        state.data = action.payload.applications;
        state.totalRecords = action.payload.totalCount;
        state.status = RequestStatus.SUCCEEDED;
      })
      .addCase(createApplication.fulfilled, (state, action) => {
        state.data.push(action.payload);
        state.status = RequestStatus.SUCCEEDED;
      })
      .addCase(updateApplication.fulfilled, (state, action) => {
        const index = state.data.findIndex((app) => app.id === action.payload.id);
        if (index !== -1) state.data[index] = action.payload;
        state.status = RequestStatus.SUCCEEDED;
      })
      .addCase(deleteApplication.fulfilled, (state, action:any) => {
        state.data = state.data.filter((app) => app.id !=  action.payload);
        state.status = RequestStatus.SUCCEEDED;
      })
      .addCase(getApplicationById.fulfilled, (state, action) => {
        // Handling single application data
        const application = action.payload.applications;
        state.data = [application]; // Assuming you're replacing the entire data with a single application
        state.status = RequestStatus.SUCCEEDED;
      })
      .addMatcher(
        (action) => action.type.endsWith('/pending'),
        (state) => {
          state.status = RequestStatus.LOADING;
        }
      )
      .addMatcher(
        (action) => action.type.endsWith('/rejected'),
        (state, action) => {
          state.status = RequestStatus.FAILED;          
        }
      );
  },
});

export default applicationsSlice.reducer;